import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Grid,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material'
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import BasicModal from '../../../../components/modal/BasicModal'
import InputComponent, {
  ResactlyInputLabel,
} from '../../../../components/ui/InputComponent'
import { useAppSelector } from '../../../../store'
import {
  selectIsLoading,
  setCampaign,
} from '../../../../store/restaurant/influencer'
import { ICampaign } from '../../../../types/influencerType'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import SelectBox from '../../../../components/ui/SelectBox'
import {
  DATE_FORMAT,
  contentNicheList,
  socialPlatforms,
} from '../../../../helpers/static-constants'
import DietaryTagInput from '../../dish/components/DietaryTagInput'
import TagInput from '../../recommendations/components/TagInput'
import { IconCalendarEvent, IconCurrencyDollar } from '@tabler/icons-react'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import RewardTip from '../../../../components/leaderboard/RewardTip'

interface CampaignsAddEditDialogProps {
  openEditModel: boolean
  setOpenEditModel: (value: boolean) => void
  campaign?: ICampaign | null
}

const formSchema = z.object({
  name: z.string().min(1, { message: 'Campaign name is required' }),
  description: z.string().min(1, { message: 'Description is required' }),

  minBudget: z.coerce.number().positive({
    message: 'Minimum budget must be a positive number',
  }),
  maxBudget: z.coerce.number().positive({
    message: 'Maximum budget must be a positive number',
  }),

  endDate: z.date({
    required_error: 'End date is required',
    invalid_type_error: 'End date must be a valid date',
  }),
  contentNiche: z.any().optional(),
  platforms: z.any().optional(),
  location: z.any().optional(),
})

const CampaignsAddEditDialog: React.FC<CampaignsAddEditDialogProps> = ({
  openEditModel,
  setOpenEditModel,
  campaign,
}) => {
  const isLoading = useAppSelector(selectIsLoading)
  const { dispatch } = useAppUtilities()

  useEffect(() => {
    if (campaign) {
      reset({
        name: campaign.name,
        description: campaign.description,
        endDate: campaign.endDate
          ? dayjs(campaign.endDate).toDate()
          : undefined,
        minBudget: campaign.budget.min,
        maxBudget: campaign.budget.max,
        contentNiche: campaign.contentNiche,
        platforms: campaign.platforms,
        location: campaign.location,
      })
    }
  }, [campaign])

  const {
    reset,
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      name: campaign?.name ?? undefined,
      description: campaign?.description ?? undefined,
      endDate: campaign?.endDate ? dayjs(campaign.endDate).toDate() : undefined,
      minBudget: Number(campaign?.budget?.min) ?? undefined,
      maxBudget: Number(campaign?.budget?.max) ?? undefined,
      contentNiche: campaign?.contentNiche ?? undefined,
      platforms: campaign?.platforms ?? undefined,
      location: campaign?.location ?? undefined,
    },
  })

  const onHandleModalEditCancel = (): void => {
    reset()
    setOpenEditModel(false)
  }

  const onHandleEdit = async (): Promise<void> => {
    await handleSubmit(
      async (data: z.infer<typeof formSchema>): Promise<void> => {
        const payload: ICampaign = {
          name: data.name,
          description: data.description,
          endDate: dayjs(data.endDate).format(DATE_FORMAT),
          budget: {
            min: data.minBudget,
            max: data.maxBudget,
          },
          contentNiche: data.contentNiche,
          platforms: data.platforms,
          location: data.location,
        }
        dispatch(setCampaign(payload))
        if (isValid) {
          reset()
          setOpenEditModel(false)
        }
      },
    )()
  }

  const selectedSocialPlatforms = watch('platforms', [])

  return (
    <BasicModal
      open={openEditModel}
      onCancel={onHandleModalEditCancel}
      onConfirm={() => {
        void onHandleEdit()
      }}
      isLoader={isLoading}
      // TODO: campaign?.name is remove
      title={
        campaign?.id ?? campaign?.name ? 'Edit Campaign' : 'Create Campaign'
      }
      buttonName={campaign?.id ?? campaign?.name ? 'Update Post' : 'Post'}
      handleClose={() => setOpenEditModel(false)}
      modelWidth="600px"
      modelOpenType="drawer"
    >
      <RewardTip
        title="Get up to 20 Points on posting a campaign"
        sx={{ mt: 1 }}
      />
      <Box my={2} width={'100%'}>
        <Box>
          <InputComponent
            label="Campaign Title"
            id="campaign-title-input"
            disabled={isLoading}
            {...register('name')}
            error={!!errors.name}
            helperText={errors.name ? errors.name.message : ' '}
            isFieldRequired
            fullWidth
            sx={{
              mt: '6px',
            }}
          />
        </Box>

        <Box>
          <InputComponent
            label="Description"
            id="campaign-description-input"
            rows={6}
            multiline
            disabled={isLoading}
            {...register('description')}
            error={!!errors.description}
            helperText={errors.description ? errors.description.message : ' '}
            isFieldRequired
            fullWidth
            sx={{
              mt: '6px',
            }}
          />
        </Box>

        <Box mb="16px">
          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <TagInput
                label="Location"
                id="location"
                value={field.value ?? []}
                onChange={field.onChange}
                isEdit
                inputRows={1}
                isSmallInput
                inputLabel="Press enter to add location"
              />
            )}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems="center"
        >
          <Box width="100%">
            <DietaryTagInput
              id="platforms"
              options={socialPlatforms}
              value={selectedSocialPlatforms}
              onChange={(_, value) =>
                setValue('platforms', [...value], { shouldValidate: true })
              }
              renderInput={(params) => (
                <InputComponent
                  {...params}
                  label="Platform"
                  id="platforms"
                  placeholder="Select option"
                  error={!!errors.platforms}
                  sx={{
                    mt: '6px',
                    mb: '16px',
                  }}
                  fullWidth
                />
              )}
            />
          </Box>

          <Box width="100%">
            <ResactlyInputLabel
              label="Influencer Niche"
              htmlFor="content-niche-select"
              labelFontSize="14px"
            />
            <Controller
              name="contentNiche"
              control={control}
              render={({ field }) => (
                <SelectBox
                  id="content-niche-select"
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="Select Influencer Niche"
                  options={contentNicheList}
                  input={<OutlinedInput />}
                  sx={{
                    mt: '4px',
                    mb: '16px',
                  }}
                />
              )}
            />
          </Box>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ResactlyInputLabel
              label="Budget"
              htmlFor="min-input"
              isFieldRequired
              labelFontSize="14px"
              isValid={!!errors.minBudget || !!errors.maxBudget}
            />
            <Stack direction="row" spacing={2}>
              <InputComponent
                id="min-input"
                disabled={isLoading}
                {...register('minBudget')}
                error={!!errors.minBudget}
                isFieldRequired
                fullWidth
                placeholder="Min"
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconCurrencyDollar stroke={2} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mt: '6px',
                }}
              />

              <InputComponent
                id="max-input"
                disabled={isLoading}
                {...register('maxBudget')}
                error={!!errors.maxBudget}
                isFieldRequired
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconCurrencyDollar stroke={2} />
                    </InputAdornment>
                  ),
                }}
                placeholder="Max"
                type="number"
                sx={{
                  mt: '6px',
                }}
              />
            </Stack>
            <Typography variant="subtitle2" color="error.main" mt={'4px'}>
              {errors.minBudget
                ? errors.minBudget.message
                : errors.maxBudget
                ? errors.maxBudget.message
                : ' '}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <ResactlyInputLabel
              label="Campaign End Date"
              htmlFor="campaignEndDate"
              isFieldRequired
              labelFontSize="14px"
              isValid={!!errors.endDate}
            />
            <Controller
              name="endDate"
              control={control}
              render={({ field, fieldState }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    {...field}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      field.onChange(newValue?.toDate())
                    }}
                    format="DD-MM-YYYY"
                    minDate={dayjs().add(1, 'day')}
                    slotProps={{
                      textField: {
                        id: 'campaignEndDate',
                        fullWidth: true,
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message ?? ' ',
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconCalendarEvent stroke={2} />
                            </InputAdornment>
                          ),
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderRadius: '8px',
                                borderColor: 'grey.300',
                                borderWidth: '1px',
                              },
                              '&:hover fieldset': {
                                borderColor: 'grey.300',
                                borderWidth: '1px',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'grey.300',
                                borderWidth: '1px',
                              },
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderRadius: '8px',
                          borderColor: 'grey.300',
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: 'grey.300',
                          borderWidth: '1px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'grey.300',
                          borderWidth: '1px',
                        },
                      },
                      '& .MuiInputBase-root': { borderRadius: '8px' },
                      '& .MuiInputBase-input': {
                        padding: '10px 10px',
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: 400,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </BasicModal>
  )
}

export default CampaignsAddEditDialog
